/**
 * Session-scoped "intent" marker for the phone-verification Nudge.
 *
 * Google/Apple login (and the password-&gt;SSO fallback) is a full-page form POST
 * (or a popup in embedded mode) that round-trips through OAuth and lands back
 * on an arbitrary destination page -- there is no success callback to hook a
 * trigger off of. Instead, every *explicit* login click writes this marker to
 * `sessionStorage` immediately before navigating away, and the post-login
 * orchestrator on the destination page consumes it once to decide whether the
 * landing was the result of an explicit login click.
 *
 * Critically, `autoLogin=true` deep links resolve through the exact same
 * `/oauth/auto-login` URL as an explicit click -- the *absence* of this marker
 * is what prevents the nudge from firing on those auto-login landings. Marker
 * set sites must never fire on the `autoLogin` path.
 *
 * Named functions (rather than inlined storage calls) so a later ticket
 * (FINTECH-4329) can instrument set/consume from one place.
 */

export const NUDGE_INTENT_KEY = 'dutchie:phoneNudgeIntent';
export const NUDGE_INTENT_TTL_MS = 5 * 60 * 1000;

/** Writes the marker (current epoch ms, as a string). Never throws. */
export function setNudgeIntentMarker(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(NUDGE_INTENT_KEY, String(Date.now()));
  } catch {
    // Safari private mode / webviews / disabled storage -- degrade to "no marker".
  }
}

/**
 * Reads the marker, then *unconditionally* removes it -- even when the value
 * is stale or malformed, a marker must never be left behind to fire on some
 * later, unrelated landing. Returns `true` only when the value parses to a
 * finite, non-future timestamp within `NUDGE_INTENT_TTL_MS` of now.
 */
export function consumeNudgeIntentMarker(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  let rawValue: string | null = null;

  try {
    rawValue = window.sessionStorage.getItem(NUDGE_INTENT_KEY);
  } catch {
    rawValue = null;
  }

  try {
    window.sessionStorage.removeItem(NUDGE_INTENT_KEY);
  } catch {
    // Nothing more we can do; treat as "no marker" below.
  }

  if (rawValue === null) {
    return false;
  }

  const timestamp = Number(rawValue);
  if (!Number.isFinite(timestamp)) {
    return false;
  }

  const now = Date.now();
  if (timestamp > now) {
    return false;
  }

  return now - timestamp <= NUDGE_INTENT_TTL_MS;
}

/** Removes the marker without consuming it. Safe to call when absent. */
export function clearNudgeIntentMarker(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.removeItem(NUDGE_INTENT_KEY);
  } catch {
    // Swallow -- absence of the marker is the desired end state either way.
  }
}
